import { create } from 'zustand';

export interface IapSuccessInfo {
  packLabel: string;
  creditsAdded: number;
  oldBalance: number;
  newBalance: number;
}

/** Ambient entitlement tier, pushed by Unity alongside `isSubscribed`.
 *  A SUPERSET of projectsBridge's `SubscriptionTier`, which is the on-demand
 *  manage-subscription vocabulary and has no non-entitled arm — the two are deliberately
 *  separate types: this one describes every user, that one only a paying one. */
export type AccountSubscriptionTier = 'lifetime' | 'yearly' | 'monthly' | 'trial' | 'free';

const ACCOUNT_SUBSCRIPTION_TIERS: readonly AccountSubscriptionTier[] = [
  'lifetime', 'yearly', 'monthly', 'trial', 'free',
];

/** Runtime guard for the tier vocabulary — the vocabulary is Unity's and can grow, so every
 *  inbound carrier (the board-route dispatch table and the Projects-route receiver chain alike)
 *  must drop an unrecognised tier rather than store a string the tier→label maps can't render. */
export function isAccountSubscriptionTier(value: unknown): value is AccountSubscriptionTier {
  return typeof value === 'string' && (ACCOUNT_SUBSCRIPTION_TIERS as readonly string[]).includes(value);
}

interface AccountState {
  accountUser: { id: string; email: string; isAnonymous: boolean; displayName: string } | null;
  accountBalance: { available: number; used: number; isBeta: boolean } | null;
  authChecked: boolean;
  authDialogOpen: boolean;
  tokenPurchaseDialogOpen: boolean;
  aiVideoDialogOpen: boolean;
  aiVideoDialogPair: { fromShotId: number; toShotId: number } | null;
  earnCreditsDialogOpen: boolean;
  iapSuccessInfo: IapSuccessInfo | null;
  /** Supabase access token passed from native app via bridge (embedded mode only) */
  nativeAccessToken: string | null;
  /** Whether the native app user has an active subscription or lifetime purchase (embedded mode). */
  isSubscribed: boolean;
  /** Which entitlement the user holds. null = NOT YET KNOWN — deliberately NOT the optimistic
   *  default `isSubscribed` carries, because this one is displayed rather than gated on: a
   *  surface reading it must render "unknown", never a guess. */
  subscriptionTier: AccountSubscriptionTier | null;
  /** App Store storefront country code from native app (e.g. "USA", "GBR"). Null until received. */
  storefrontCountryCode: string | null;
  /** Whether the user's App Store region is US. Null until received. Defaults to US behavior. */
  isUSStorefront: boolean | null;
  /** Editor standalone auth: embedded mode using browser Supabase session instead of native JWT. */
  standaloneAuthMode: boolean;
  /** Cached purchase history check — null = not yet checked, true/false = result */
  hasEverPurchased: boolean | null;
  /** Session flag — set on dismiss, prevents re-showing depletion dialog this session */
  welcomeDepletionDismissedThisSession: boolean;
  /** Whether to show the WelcomeDepletionDialog */
  welcomeDepletionOpen: boolean;
  /** 20260928 - The What's New dot (native parity: the Unity ellipsis menu badges the row while the
   *  roadmap has items the user has not opened). Pushed by Unity as `setWhatsNewBadge {hasNew}` on
   *  every `ready` and on the roadmap's availability event; false until a push lands. One field for
   *  every host that draws the row — the board's hamburger and panel, the Projects home's hamburger
   *  and the canvas overlay's menu — so the dot cannot disagree between them. */
  whatsNewBadge: boolean;

  checkHasEverPurchased: () => Promise<boolean>;
  setWelcomeDepletionDismissed: () => void;
  setWelcomeDepletionOpen: (open: boolean) => void;

  setAccountUser: (user: AccountState['accountUser']) => void;
  setAccountBalance: (balance: AccountState['accountBalance']) => void;
  setAuthChecked: (checked: boolean) => void;
  refreshBalance: () => Promise<void>;
  setAuthDialogOpen: (open: boolean) => void;
  setTokenPurchaseDialogOpen: (open: boolean) => void;
  setEarnCreditsDialogOpen: (open: boolean) => void;
  openAiVideoDialog: (pair?: { fromShotId: number; toShotId: number }) => void;
  closeAiVideoDialog: () => void;
  showIapSuccess: (info: IapSuccessInfo) => void;
  dismissIapSuccess: () => void;
  setNativeAccessToken: (token: string | null) => void;
  setSubscribed: (subscribed: boolean) => void;
  setSubscriptionTier: (tier: AccountSubscriptionTier | null) => void;
  setStorefrontRegion: (countryCode: string, isUS: boolean) => void;
  setStandaloneAuthMode: (enabled: boolean) => void;
  setWhatsNewBadge: (hasNew: boolean) => void;
}

export const useAccountStore = create<AccountState>((set) => ({
  accountUser: null,
  accountBalance: null,
  authChecked: false,
  authDialogOpen: false,
  tokenPurchaseDialogOpen: false,
  aiVideoDialogOpen: false,
  aiVideoDialogPair: null,
  earnCreditsDialogOpen: false,
  iapSuccessInfo: null,
  nativeAccessToken: null,
  isSubscribed: true,
  subscriptionTier: null,
  storefrontCountryCode: null,
  isUSStorefront: null,
  standaloneAuthMode: false,
  hasEverPurchased: null,
  welcomeDepletionDismissedThisSession: false,
  welcomeDepletionOpen: false,
  whatsNewBadge: false,

  setAccountUser: (user) => set({ accountUser: user }),
  setAccountBalance: (balance) => set({ accountBalance: balance }),
  setAuthChecked: (checked) => set({ authChecked: checked }),
  refreshBalance: async () => {
    try {
      const { getBalance } = await import('@/api/aiApi.ts');
      const bal = await getBalance();
      set({ accountBalance: bal });
    } catch (err) {
      console.warn('Failed to refresh balance:', err);
    }
  },
  setAuthDialogOpen: (open) => set({ authDialogOpen: open }),
  setTokenPurchaseDialogOpen: (open) => set({ tokenPurchaseDialogOpen: open }),
  setEarnCreditsDialogOpen: (open) => set({ earnCreditsDialogOpen: open }),
  openAiVideoDialog: (pair) => set({ aiVideoDialogOpen: true, aiVideoDialogPair: pair ?? null }),
  closeAiVideoDialog: () => set({ aiVideoDialogOpen: false, aiVideoDialogPair: null }),
  showIapSuccess: (info) => set({ iapSuccessInfo: info }),
  dismissIapSuccess: () => set({ iapSuccessInfo: null }),
  setNativeAccessToken: (token) => set({ nativeAccessToken: token }),
  setSubscribed: (subscribed) => set({ isSubscribed: subscribed }),
  setSubscriptionTier: (tier) => set({ subscriptionTier: tier }),
  setStorefrontRegion: (countryCode, isUS) => set({ storefrontCountryCode: countryCode, isUSStorefront: isUS }),
  setStandaloneAuthMode: (enabled) => set({ standaloneAuthMode: enabled }),
  setWhatsNewBadge: (hasNew) => set({ whatsNewBadge: hasNew }),
  checkHasEverPurchased: async (): Promise<boolean> => {
    const cached: boolean | null = useAccountStore.getState().hasEverPurchased;
    if (cached !== null) return cached;
    try {
      const { getPurchaseHistory } = await import('@/api/stripeApi.ts');
      const history = await getPurchaseHistory();
      const result = history.length > 0;
      set({ hasEverPurchased: result });
      return result;
    } catch {
      // On error, assume never purchased (show the dialog)
      return false;
    }
  },
  setWelcomeDepletionDismissed: () => set({ welcomeDepletionDismissedThisSession: true, welcomeDepletionOpen: false }),
  setWelcomeDepletionOpen: (open) => set({ welcomeDepletionOpen: open }),
}));
